import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Stack,
    Grid,
    Button,
    Modal,
    Slide,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    TextField,
    InputAdornment,
    Divider,
    Tooltip,
    Chip
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { ChartOptions, ChartData } from 'chart.js';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ViewInArIcon from '@mui/icons-material/ViewInAr';

// --- Interfaces ---
export interface Trace {
    id: string;
    timestamp: string;
    name: string;
    latency: number;
    cost: number;
    totalTokens: number;
    input: string;
    output: string;
    metadata: string;
}

// --- Format Helper ---
const formatJSON = (obj: any) => JSON.stringify(obj, null, 2);

// --- Realistic Prompt/Response Pools (Updated with AIVerse Responses) ---
const SUPPORT_PROMPTS = [
    {
        input: [{"role": "system", "content": "You are a helpful support assistant for Faraday. Provide detailed, technical answers."}, {"role": "user", "content": "I keep getting a 500 error when trying to deploy my Kubernetes cluster. What should I do?"}],
        output: {"role": "assistant", "content": `### 1. Check IAM and Role Binding Permissions

A \`500 Internal Server Error\` during cluster provisioning often masks a \`403 Forbidden\` at the provider level.

**Steps to resolve:**
1. Verify that the deployment service account has the necessary permissions (e.g., \`Kubernetes Engine Admin\` for GCP or \`EKSClusterPolicy\` for AWS).
2. Run a dry-run using the CLI:
\`\`\`bash
faraday cluster deploy --dry-run
\`\`\`
This will surface underlying permission faults.

### 2. Verify VPC and Subnet Configurations

Cluster deployments frequently fail with a 500 if the underlying network topology is exhausted or misconfigured.

* **Secondary IP Ranges:** Ensure your Pod and Service IP ranges do not overlap with existing subnets.
* **IP Exhaustion:** Check if your chosen subnet has enough free IPs for the requested node pool.

\`\`\`bash
# Check available IPs in your AWS subnet
aws ec2 describe-subnets --subnet-ids subnet-12345 --query "Subnets[*].AvailableIpAddressCount"
\`\`\`

### 3. Resource Quotas and Limits

You might be hitting a regional quota for vCPUs, persistent disks, or in-use IP addresses.

**Best Practices:**
* Navigate to your cloud provider's Quota Dashboard.
* Check the usage for your specific region (e.g., \`us-east-1\`).
* If you are provisioning GPU nodes, verify that you have explicit quota granted for that instance family.

### 4. Control Plane Timeouts

Sometimes, the cloud provider's API control plane experiences intermittent degradation, returning a 500 error when the API gateway times out.

**Recommended Action:**
1. Check the status page of your cloud provider (AWS Health Dashboard / GCP Status).
2. Implement an exponential backoff retry strategy in your CI/CD pipeline.
3. Try deploying into a different availability zone if the issue persists.`}
    },
    {
        input: [{"role": "system", "content": "Use the provided RAG context to answer the user. Context: [DOCUMENT: Pricing Plan. The Pro plan includes API access with a rate limit of 1000 requests per minute...]"}, {"role": "user", "content": "Does the Pro plan include API access? And what are the rate limits?"}],
        output: {"role": "assistant", "content": "Yes, according to our pricing documentation, the Pro plan includes API access. The rate limit for the Pro plan is 1,000 requests per minute."}
    },
    {
        input: [{"role": "system", "content": "You are a helpful support assistant."}, {"role": "user", "content": "How do I reset my password?"}],
        output: {"role": "assistant", "content": "To reset your password, please visit the settings page, navigate to the 'Security' tab, and click on 'Reset Password'. A link will be sent to your registered email."}
    },
    {
        input: [{"role": "system", "content": "You are a helpful support assistant."}, {"role": "user", "content": "Where can I find the documentation for the CLI tool?"}],
        output: {"role": "assistant", "content": `### Official CLI Documentation

You can find the comprehensive documentation for the Faraday CLI at our official developer portal:

🔗 **[https://docs.faraday.com/cli/getting-started](https://docs.faraday.com/cli/getting-started)**

**Quick Install:**
\`\`\`bash
curl -sL https://cli.faraday.com/install.sh | bash
\`\`\`
After installation, run \`faraday --help\` to see the list of available commands.

### CLI Authentication Guide

The documentation is located at [https://docs.faraday.com/cli](https://docs.faraday.com/cli). 

For most users, the first step is setting up authentication. You can authenticate interactively or via environment variables for headless environments:

\`\`\`bash
# Interactive login
faraday login

# CI/CD headless login
export FARADAY_API_KEY="your_api_key_here"
faraday cluster list
\`\`\`

### Advanced CLI Usage & Troubleshooting

All CLI reference materials can be found at **[https://docs.faraday.com/cli/reference](https://docs.faraday.com/cli/reference)**.

If you are experiencing issues, you can append the \`--verbose\` or \`--debug\` flag to any command to get detailed stack traces and API response logs:

\`\`\`bash
faraday deploy my-service --verbose
\`\`\`
This is highly recommended before opening a support ticket.

### CI/CD Integration Documentation

If you are looking to integrate the CLI into your automated pipelines, visit **[https://docs.faraday.com/cli/ci-cd](https://docs.faraday.com/cli/ci-cd)**.

We provide native GitHub Actions, GitLab CI templates, and CircleCI orbs. Here is a basic GitHub Action snippet:

\`\`\`yaml
steps:
  - name: Install Faraday CLI
    uses: faraday/cli-setup-action@v2
    with:
      api-key: \${{ secrets.FARADAY_API_KEY }}
\`\`\``}
    }
];

const ANALYTICS_PROMPTS = [
    {
        input: [{"role": "system", "content": "Convert user questions into highly optimized PostgreSQL queries based on the provided schema."}, {"role": "user", "content": "Show me the top 5 customers by revenue in Q3 2025."}],
        output: {"role": "assistant", "content": "```sql\nSELECT c.name, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.id = o.customer_id \nWHERE o.order_date BETWEEN '2025-07-01' AND '2025-09-30' \nGROUP BY c.id, c.name \nORDER BY revenue DESC \nLIMIT 5;\n```"}
    },
    {
        input: [{"role": "system", "content": "Analyze the CSV data provided and summarize the correlation."}, {"role": "user", "content": "Here is the dataset: [CSV DATA EXCEEDING CONTEXT WINDOW...]"}],
        output: {"error": {"message": "Context length exceeded. The provided CSV data exceeds the maximum token limit of 128k for this model.", "type": "invalid_request_error", "param": "messages", "code": "context_length_exceeded"}}
    },
    {
        input: [{"role": "system", "content": "You are a data analysis agent."}, {"role": "user", "content": "Compare user retention between Jan and Feb."}],
        output: {"role": "assistant", "content": "Based on the internal event logs, user retention in January was 45% after 30 days, compared to 48% in February. This represents a 6.6% relative increase month-over-month."}
    },
    {
        input: [{"role": "system", "content": "You are a data analysis agent."}, {"role": "user", "content": "Generate a weekly active users report for the last month."}],
        output: {"role": "assistant", "content": "Here is your WAU report for the last 4 weeks:\n- Week 1: 12,450\n- Week 2: 13,100\n- Week 3: 12,800\n- Week 4: 14,200\nOverall growth trend is positive at +14%."}
    }
];

// --- Deterministic PRNG to ensure data is exactly the same every single render ---
const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
};

// --- Generate 4,520 highly realistic, constant traces ---
const generateConstantMockTraces = (): Trace[] => {
    const traces: Trace[] = [];
    const baseTime = new Date('2026-04-16T15:24:00Z').getTime();
    
    const totalSupport = 3420;
    const totalAnalytics = 1100;
    const totalTraces = totalSupport + totalAnalytics;

    for (let i = 0; i < totalTraces; i++) {
        const isSupport = i < totalSupport;
        const name = isSupport ? 'Support Agent' : 'Analytics Agent';
        
        const rand1 = seededRandom(i + 1);
        const rand2 = seededRandom(i + 2);
        const rand3 = seededRandom(i + 3);
        const rand4 = seededRandom(i + 4);

        const latency = isSupport ? (rand1 * 1.5 + 0.5) : (rand1 * 6.0 + 2.0);
        const tokens = isSupport ? Math.floor(rand2 * 200 + 50) : Math.floor(rand2 * 2000 + 500);
        const cost = tokens * (isSupport ? 0.000005 : 0.000015);
        
        const randomTimeOffset = rand3 * 7 * 24 * 60 * 60 * 1000; 
        
        const promptPool = isSupport ? SUPPORT_PROMPTS : ANALYTICS_PROMPTS;
        const selectedPrompt = promptPool[Math.floor(rand4 * promptPool.length)];
        
        const isError = 'error' in selectedPrompt.output;

        const traceId = `trace_${Math.floor(seededRandom(i + 5) * 1e8).toString(16)}-${Math.floor(seededRandom(i + 6) * 1e4).toString(16)}-4a2c-9b1d-${Math.floor(seededRandom(i + 7) * 1e12).toString(16)}`;

        traces.push({
            id: traceId,
            timestamp: new Date(baseTime - randomTimeOffset).toISOString(),
            name: name,
            latency: parseFloat(latency.toFixed(2)),
            cost: parseFloat(cost.toFixed(4)),
            totalTokens: tokens,
            input: formatJSON(selectedPrompt.input),
            output: formatJSON(selectedPrompt.output),
            metadata: formatJSON({
                "model": isSupport ? "gemini-3.1-pro-preview" : "gemini-3.1-pro-preview",
                "temperature": isSupport ? 0.2 : 0.0,
                "environment": "production",
                "error": isError ? true : undefined,
                "usage": { 
                    "prompt_tokens": Math.floor(tokens * 0.4), 
                    "completion_tokens": isError ? 0 : Math.floor(tokens * 0.6) 
                },
                "source": isSupport ? "api" : "internal_dashboard",
                "langfuse_session_id": `sess_${Math.floor(seededRandom(i+8) * 1e6).toString(16)}`
            })
        });
    }

    return traces.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

const MOCK_TRACES_LIST: Trace[] = generateConstantMockTraces();

const MOCK_AGENTS = ['Support Agent', 'Analytics Agent'];
const MOCK_TRACE_SPLIT = [
    { name: MOCK_AGENTS[0], count: 3420, color: '#81C784' },
    { name: MOCK_AGENTS[1], count: 1100, color: '#BA68C8' }
];

const MOCK_TRACES_TIME_SERIES = {
    labels: ['Apr 10', 'Apr 11', 'Apr 12', 'Apr 13', 'Apr 14', 'Apr 15', 'Apr 16'],
    data: [520, 610, 590, 780, 630, 650, 740] 
};

// --- Trace Detail Panel Component ---
const TraceDetailPanel: React.FC<{ open: boolean; onClose: () => void; trace: Trace | null }> = ({ open, onClose, trace }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!trace) return null;

    const DetailCard = ({ title, content, color = '#475467' }: { title: string, content: string, color?: string }) => (
        <Box sx={{ border: '1px solid #EAECF0', borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 16px', bgcolor: '#F3F3F3', borderBottom: '1px solid #EAECF0' }}>
                <Typography sx={{ fontWeight: 600, color: '#101828', fontFamily: 'monospace', fontSize: '12px' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy"}>
                    <IconButton size="small" onClick={() => handleCopy(content)}><ContentCopyIcon sx={{ fontSize: '14px', color: '#666' }} /></IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2, bgcolor: '#F9FAFB', maxHeight: '300px', overflowY: 'auto' }}>
                <Typography sx={{ color: color, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px' }}>{content}</Typography>
            </Box>
        </Box>
    );

    return (
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
            <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: { xs: '100%', md: '600px' }, zIndex: 1300, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 12px rgba(0,0,0,0.1)', borderLeft: '1px solid #EAECF0', bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #EAECF0' }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#101828' }}>Trace Details</Typography>
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Box>
                <Box sx={{ overflowY: 'auto', p: 3, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', mb: 3 }}>
                        <Typography sx={{ fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#333333', mr: 2, fontSize: '18px' }}>
                            {trace.id}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '13px', color: '#666666' }}>
                            {new Date(trace.timestamp).toLocaleString()}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3, rowGap: 1 }}>
                        <Chip icon={<ViewInArIcon />} label={trace.name} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
                        <Chip icon={<AccessTimeIcon />} label={`${trace.latency}s`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
                        <Chip icon={<ConfirmationNumberOutlinedIcon />} label={`${trace.totalTokens} tokens`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
                        <Chip icon={<CreditCardOutlinedIcon />} label={`$${trace.cost.toFixed(4)}`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
                    </Stack>

                    <Divider sx={{ mb: 3 }} />
                    <DetailCard title="Input Payload" content={trace.input} />
                    <DetailCard title="Output Payload" content={trace.output} color={trace.output.includes("error") ? "#D92D20" : "#039855"} />
                    <DetailCard title="Metadata & Usage" content={trace.metadata} />
                </Box>
            </Box>
        </Slide>
    );
};

// --- Main Traces Section Component ---
const LLMTracesSection: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);
    
    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Area Chart Options (Chart.js)
    const areaChartData: ChartData<'line'> = {
        labels: MOCK_TRACES_TIME_SERIES.labels,
        datasets: [{
            label: 'Traces',
            data: MOCK_TRACES_TIME_SERIES.data,
            borderColor: '#0064FF',
            backgroundColor: 'rgba(0, 100, 255, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 5,
            borderWidth: 2,
        }]
    };

    const areaChartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#333',
                bodyColor: '#333',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                titleFont: { family: 'Gilroy-Bold' },
                bodyFont: { family: 'Gilroy-Medium' },
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { family: 'Gilroy-Regular', size: 11 } } },
            y: { grid: { display: true, color: '#f0f0f0' }, ticks: { font: { family: 'Gilroy-Regular', size: 11 } }, beginAtZero: true }
        }
    };

    const maxSplitValue = Math.max(...MOCK_TRACE_SPLIT.map(d => d.count), 1);
    
    // Filter traces based on search
    const filteredTraces = useMemo(() => {
        return MOCK_TRACES_LIST.filter(t => 
            t.id.toLowerCase().includes(searchQuery.toLowerCase()) || 
            t.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    // Paginate the filtered traces
    const paginatedTraces = useMemo(() => {
        return filteredTraces.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredTraces, page, rowsPerPage]);

    // Handle Pagination Changes
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Reset pagination when search query changes
    React.useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    return (
        <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
                {/* Traces By Time Chart */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '12px', height: '350px' }}>
                        <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold", mb: 2 }}>Traces By Time</Typography>
                        <Box sx={{ height: 'calc(100% - 40px)' }}>
                            <Line data={areaChartData} options={areaChartOptions} />
                        </Box>
                    </Paper>
                </Grid>

                {/* Traces Split */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '12px', height: '350px', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Traces</Typography>
                            <Button variant="text" onClick={() => setIsModalOpen(true)} sx={{ textTransform: 'none', fontFamily: 'Gilroy-SemiBold', color: '#0064FF' }}>
                                View Details
                            </Button>
                        </Box>
                        <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '24px', mb: 0.5 }}>
                            4,520 <Typography component="span" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'text.secondary' }}>Total traces tracked</Typography>
                        </Typography>

                        <Stack spacing={2} sx={{ mt: 3, flexGrow: 1, justifyContent: 'center' }}>
                            {MOCK_TRACE_SPLIT.map((item) => {
                                const widthPercent = (item.count / maxSplitValue) * 100;
                                return (
                                    <Box key={item.name} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Box sx={{ background: `linear-gradient(to right, ${item.color}80, ${item.color})`, borderRadius: '6px', p: '12px', width: `${widthPercent}%`, minWidth: 'fit-content' }}>
                                                <Typography sx={{ fontSize: 14, fontFamily: 'Gilroy-SemiBold', color: '#fff' }}>{item.name}</Typography>
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: 14, fontFamily: 'Gilroy-Bold', color: '#333', pl: 2 }}>{item.count.toLocaleString()}</Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            {/* Traces Details Modal */}
            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Slide direction="up" in={isModalOpen}>
                    <Paper sx={{ position: 'relative', width: '90%', maxWidth: '1200px', height: '85vh', borderRadius: '16px', outline: 'none', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        
                        {/* Modal Header */}
                        <Box sx={{ p: 3, borderBottom: '1px solid #EAECEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F9FAFB', flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 40, height: 40, bgcolor: '#E5EFFF', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ViewInArIcon sx={{ color: '#0064FF' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#333' }}>Traces Database</Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Gilroy-Regular' }}>Track detailed logs, latency, and costs of every trace</Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => setIsModalOpen(false)}><CloseIcon /></IconButton>
                        </Box>

                        {/* Modal Body (added minHeight: 0 to ensure proper nested scrolling) */}
                        <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff', position: 'relative', minHeight: 0 }}>
                            <Box sx={{ mb: 3, flexShrink: 0 }}>
                                <TextField
                                    size="small"
                                    placeholder="Search by trace ID or name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                                        sx: { borderRadius: '8px', fontFamily: 'Gilroy-Regular', width: '300px', bgcolor: '#F5F5F5', '& fieldset': { border: 'none' } }
                                    }}
                                />
                            </Box>

                            {/* Added minHeight: 0 to allow flex container to scroll content */}
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EAECF0', borderRadius: '8px', flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Trace ID</TableCell>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Name</TableCell>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Timestamp</TableCell>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Latency</TableCell>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Tokens</TableCell>
                                            <TableCell sx={{ bgcolor: '#F9FAFB', fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Cost</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedTraces.map((trace) => (
                                            <TableRow key={trace.id} hover onClick={() => setSelectedTrace(trace)} sx={{ cursor: 'pointer' }}>
                                                <TableCell sx={{ fontFamily: 'monospace', color: '#0064FF' }}>{trace.id}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Gilroy-Medium', color: '#333' }}>{trace.name}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{new Date(trace.timestamp).toLocaleString()}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{trace.latency}s</TableCell>
                                                <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{trace.totalTokens}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>${trace.cost.toFixed(4)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {paginatedTraces.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, fontFamily: 'Gilroy-Regular', color: '#666' }}>No traces found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            {/* Pagination Controls - flexShrink 0 added to keep it pinned to bottom */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={filteredTraces.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ borderTop: '1px solid #EAECF0', fontFamily: 'Gilroy-Regular', flexShrink: 0 }}
                            />
                        </Box>

                        {/* Slide-out Panel for Detail */}
                        <TraceDetailPanel open={!!selectedTrace} onClose={() => setSelectedTrace(null)} trace={selectedTrace} />
                    </Paper>
                </Slide>
            </Modal>
        </Box>
    );
};

export default LLMTracesSection;

// import React, { useState } from 'react';
// import {
//     Box,
//     Typography,
//     Stack,
//     Grid,
//     Button,
//     Modal,
//     Slide,
//     IconButton,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Paper,
//     TextField,
//     InputAdornment,
//     Divider,
//     Tooltip,
//     Chip
// } from '@mui/material';
// import { Line } from 'react-chartjs-2';
// import { ChartOptions, ChartData } from 'chart.js';
// import CloseIcon from '@mui/icons-material/Close';
// import SearchIcon from '@mui/icons-material/Search';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
// import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
// import ViewInArIcon from '@mui/icons-material/ViewInAr';

// // --- Highly Realistic Langfuse/ClickHouse Mock Data ---
// const MOCK_AGENTS = ['Support Agent', 'Analytics Agent'];

// const MOCK_TRACE_SPLIT = [
//     { name: MOCK_AGENTS[0], count: 3420, color: '#81C784' },
//     { name: MOCK_AGENTS[1], count: 1100, color: '#BA68C8' }
// ];

// const MOCK_TRACES_TIME_SERIES = {
//     labels: ['May 1', 'May 2', 'May 3', 'May 4', 'May 5', 'May 6', 'May 7'],
//     data: [150, 230, 180, 320, 290, 410, 380]
// };

// interface Trace {
//     id: string;
//     timestamp: string;
//     name: string;
//     latency: number;
//     cost: number;
//     totalTokens: number;
//     input: string;
//     output: string;
//     metadata: string;
// }

// // Formatting helper to make JSON look nice in the trace details
// const formatJSON = (obj: any) => JSON.stringify(obj, null, 2);

// const MOCK_TRACES_LIST: Trace[] = [
//     { 
//         id: 'trace_8f9a2b1c-4d3e-4567-890a', 
//         timestamp: '2026-05-07T10:23:45.123Z', 
//         name: 'Support Agent', 
//         latency: 1.84, 
//         cost: 0.0015, 
//         totalTokens: 145, 
//         input: formatJSON([
//             { "role": "system", "content": "You are a helpful support assistant for Faraday. Keep answers concise." },
//             { "role": "user", "content": "I keep getting a 500 error when trying to deploy my Kubernetes cluster. What should I do?" }
//         ]), 
//         output: formatJSON({
//             "role": "assistant",
//             "content": "I apologize for the inconvenience. A 500 error during cluster deployment usually indicates a temporary provider issue or a missing IAM permission. Could you please provide your specific Project ID and the deployment logs so I can look into it?"
//         }), 
//         metadata: formatJSON({
//             "model": "gpt-4o",
//             "temperature": 0.2,
//             "environment": "production",
//             "prompt_id": "prompt_support_v2.1",
//             "usage": { "prompt_tokens": 45, "completion_tokens": 100 },
//             "source": "api"
//         })
//     },
//     { 
//         id: 'trace_5a4b3c2d-1122-3344-5566', 
//         timestamp: '2026-05-07T10:21:12.845Z', 
//         name: 'Analytics Agent', 
//         latency: 4.12, 
//         cost: 0.0085, 
//         totalTokens: 210, 
//         input: formatJSON([
//             { "role": "system", "content": "Convert user questions into highly optimized PostgreSQL queries based on the provided schema." },
//             { "role": "user", "content": "Show me the top 5 customers by revenue in Q3 2025." }
//         ]), 
//         output: formatJSON({
//             "role": "assistant",
//             "content": "```sql\nSELECT c.name, SUM(o.total_amount) as revenue \nFROM customers c \nJOIN orders o ON c.id = o.customer_id \nWHERE o.order_date BETWEEN '2025-07-01' AND '2025-09-30' \nGROUP BY c.id, c.name \nORDER BY revenue DESC \nLIMIT 5;\n```"
//         }), 
//         metadata: formatJSON({
//             "model": "claude-3-opus-20240229",
//             "temperature": 0.0,
//             "environment": "production",
//             "prompt_id": "prompt_text_to_sql_v1.0",
//             "usage": { "prompt_tokens": 120, "completion_tokens": 90 },
//             "source": "internal_dashboard"
//         })
//     },
//     { 
//         id: 'trace_1q2w3e4r-abcd-efgh-ijkl', 
//         timestamp: '2026-05-07T10:15:00.002Z', 
//         name: 'Support Agent', 
//         latency: 2.50, 
//         cost: 0.0030, 
//         totalTokens: 850, 
//         input: formatJSON([
//             { "role": "system", "content": "Use the provided RAG context to answer the user. Context: [DOCUMENT: Faraday Pricing Plan. The Pro plan includes API access with a rate limit of 1000 requests per minute...]" },
//             { "role": "user", "content": "Does the Pro plan include API access? And what are the rate limits?" }
//         ]), 
//         output: formatJSON({
//             "role": "assistant",
//             "content": "Yes, according to our pricing documentation, the Pro plan includes API access. The rate limit for the Pro plan is 1,000 requests per minute."
//         }), 
//         metadata: formatJSON({
//             "model": "gemini-1.5-pro",
//             "temperature": 0.5,
//             "retrieval": {
//                 "chunks_retrieved": 3,
//                 "vector_db_latency_ms": 145,
//                 "similarity_score": 0.89
//             },
//             "usage": { "prompt_tokens": 800, "completion_tokens": 50 },
//             "source": "slack_bot"
//         })
//     },
//     { 
//         id: 'trace_0p9o8i7u-9876-5432-10ab', 
//         timestamp: '2026-05-07T10:05:30.555Z', 
//         name: 'Analytics Agent', 
//         latency: 8.95, 
//         cost: 0.0640, 
//         totalTokens: 128005, 
//         input: formatJSON([
//             { "role": "system", "content": "Analyze the CSV data provided and summarize the correlation." },
//             { "role": "user", "content": "Here is the dataset: [CSV DATA EXCEEDING CONTEXT WINDOW...]" }
//         ]), 
//         output: formatJSON({
//             "error": {
//                 "message": "Context length exceeded. The provided CSV data exceeds the maximum token limit of 128k for this model.",
//                 "type": "invalid_request_error",
//                 "param": "messages",
//                 "code": "context_length_exceeded"
//             }
//         }), 
//         metadata: formatJSON({
//             "model": "gpt-3.5-turbo",
//             "temperature": 0.1,
//             "environment": "development",
//             "error": true,
//             "usage": { "prompt_tokens": 128005, "completion_tokens": 0 },
//             "source": "api"
//         })
//     },
// ];

// // --- Trace Detail Panel Component ---
// const TraceDetailPanel: React.FC<{ open: boolean; onClose: () => void; trace: Trace | null }> = ({ open, onClose, trace }) => {
//     const [copied, setCopied] = useState(false);

//     const handleCopy = (text: string) => {
//         navigator.clipboard.writeText(text);
//         setCopied(true);
//         setTimeout(() => setCopied(false), 2000);
//     };

//     if (!trace) return null;

//     const DetailCard = ({ title, content, color = '#475467' }: { title: string, content: string, color?: string }) => (
//         <Box sx={{ border: '1px solid #EAECF0', borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 16px', bgcolor: '#F3F3F3', borderBottom: '1px solid #EAECF0' }}>
//                 <Typography sx={{ fontWeight: 600, color: '#101828', fontFamily: 'monospace', fontSize: '12px' }}>{title}</Typography>
//                 <Tooltip title={copied ? "Copied!" : "Copy"}>
//                     <IconButton size="small" onClick={() => handleCopy(content)}><ContentCopyIcon sx={{ fontSize: '14px', color: '#666' }} /></IconButton>
//                 </Tooltip>
//             </Box>
//             <Box sx={{ p: 2, bgcolor: '#F9FAFB' }}>
//                 <Typography sx={{ color: color, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px' }}>{content}</Typography>
//             </Box>
//         </Box>
//     );

//     return (
//         <Slide direction="left" in={open} mountOnEnter unmountOnExit>
//             <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: { xs: '100%', md: '600px' }, zIndex: 1300, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 12px rgba(0,0,0,0.1)', borderLeft: '1px solid #EAECF0', bgcolor: 'white' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #EAECF0' }}>
//                     <Typography sx={{ fontSize: "16px", fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#101828' }}>Trace Details</Typography>
//                     <IconButton onClick={onClose}><CloseIcon /></IconButton>
//                 </Box>
//                 <Box sx={{ overflowY: 'auto', p: 3, flexGrow: 1 }}>
//                     <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', mb: 3 }}>
//                         <Typography sx={{ fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#333333', mr: 2, fontSize: '18px' }}>
//                             {trace.id}
//                         </Typography>
//                         <Typography sx={{ fontFamily: 'Gilroy-Regular', fontSize: '13px', color: '#666666' }}>
//                             {new Date(trace.timestamp).toLocaleString()}
//                         </Typography>
//                     </Box>

//                     <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3, rowGap: 1 }}>
//                         <Chip icon={<ViewInArIcon />} label={trace.name} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
//                         <Chip icon={<AccessTimeIcon />} label={`${trace.latency}s`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
//                         <Chip icon={<ConfirmationNumberOutlinedIcon />} label={`${trace.totalTokens} tokens`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
//                         <Chip icon={<CreditCardOutlinedIcon />} label={`$${trace.cost.toFixed(4)}`} size="small" sx={{ fontFamily: 'Gilroy-Medium', bgcolor: '#F2F4F7' }} />
//                     </Stack>

//                     <Divider sx={{ mb: 3 }} />
//                     <DetailCard title="Input Payload" content={trace.input} />
//                     <DetailCard title="Output Payload" content={trace.output} color={trace.output.includes("error") ? "#D92D20" : "#039855"} />
//                     <DetailCard title="Metadata & Usage" content={trace.metadata} />
//                 </Box>
//             </Box>
//         </Slide>
//     );
// };

// // --- Main Traces Section Component ---
// const LLMTracesSection: React.FC = () => {
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [searchQuery, setSearchQuery] = useState('');
//     const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);

//     // Area Chart Options (Chart.js)
//     const areaChartData: ChartData<'line'> = {
//         labels: MOCK_TRACES_TIME_SERIES.labels,
//         datasets: [{
//             label: 'Traces',
//             data: MOCK_TRACES_TIME_SERIES.data,
//             borderColor: '#0064FF',
//             backgroundColor: 'rgba(0, 100, 255, 0.1)',
//             fill: true,
//             tension: 0.4,
//             pointRadius: 0,
//             pointHoverRadius: 5,
//             borderWidth: 2,
//         }]
//     };

//     const areaChartOptions: ChartOptions<'line'> = {
//         responsive: true,
//         maintainAspectRatio: false,
//         plugins: {
//             legend: { display: false },
//             tooltip: {
//                 backgroundColor: 'rgba(255, 255, 255, 0.95)',
//                 titleColor: '#333',
//                 bodyColor: '#333',
//                 borderColor: '#e0e0e0',
//                 borderWidth: 1,
//                 titleFont: { family: 'Gilroy-Bold' },
//                 bodyFont: { family: 'Gilroy-Medium' },
//             }
//         },
//         scales: {
//             x: { grid: { display: false }, ticks: { font: { family: 'Gilroy-Regular', size: 11 } } },
//             y: { grid: { display: true, color: '#f0f0f0' }, ticks: { font: { family: 'Gilroy-Regular', size: 11 } }, beginAtZero: true }
//         }
//     };

//     const maxSplitValue = Math.max(...MOCK_TRACE_SPLIT.map(d => d.count), 1);
//     const filteredTraces = MOCK_TRACES_LIST.filter(t => t.id.toLowerCase().includes(searchQuery.toLowerCase()) || t.name.toLowerCase().includes(searchQuery.toLowerCase()));

//     return (
//         <Box sx={{ mt: 3 }}>
//             <Grid container spacing={3}>
//                 {/* Traces By Time Chart */}
//                 <Grid item xs={12} md={7}>
//                     <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '12px', height: '350px' }}>
//                         <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold", mb: 2 }}>Traces By Time</Typography>
//                         <Box sx={{ height: 'calc(100% - 40px)' }}>
//                             <Line data={areaChartData} options={areaChartOptions} />
//                         </Box>
//                     </Paper>
//                 </Grid>

//                 {/* Traces Split */}
//                 <Grid item xs={12} md={5}>
//                     <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '12px', height: '350px', display: 'flex', flexDirection: 'column' }}>
//                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                             <Typography variant="h6" sx={{ fontFamily: "Gilroy-Bold" }}>Traces</Typography>
//                             <Button variant="text" onClick={() => setIsModalOpen(true)} sx={{ textTransform: 'none', fontFamily: 'Gilroy-SemiBold', color: '#0064FF' }}>
//                                 View Details
//                             </Button>
//                         </Box>
//                         <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '24px', mb: 0.5 }}>
//                             4,520 <Typography component="span" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '14px', color: 'text.secondary' }}>Total traces tracked</Typography>
//                         </Typography>

//                         <Stack spacing={2} sx={{ mt: 3, flexGrow: 1, justifyContent: 'center' }}>
//                             {MOCK_TRACE_SPLIT.map((item) => {
//                                 const widthPercent = (item.count / maxSplitValue) * 100;
//                                 return (
//                                     <Box key={item.name} sx={{ display: 'flex', alignItems: 'center' }}>
//                                         <Box sx={{ flexGrow: 1 }}>
//                                             <Box sx={{ background: `linear-gradient(to right, ${item.color}80, ${item.color})`, borderRadius: '6px', p: '12px', width: `${widthPercent}%`, minWidth: 'fit-content' }}>
//                                                 <Typography sx={{ fontSize: 14, fontFamily: 'Gilroy-SemiBold', color: '#fff' }}>{item.name}</Typography>
//                                             </Box>
//                                         </Box>
//                                         <Typography sx={{ fontSize: 14, fontFamily: 'Gilroy-Bold', color: '#333', pl: 2 }}>{item.count.toLocaleString()}</Typography>
//                                     </Box>
//                                 );
//                             })}
//                         </Stack>
//                     </Paper>
//                 </Grid>
//             </Grid>

//             {/* Traces Details Modal */}
//             <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                 <Slide direction="up" in={isModalOpen}>
//                     <Paper sx={{ position: 'relative', width: '90%', maxWidth: '1200px', height: '85vh', borderRadius: '16px', outline: 'none', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        
//                         {/* Modal Header */}
//                         <Box sx={{ p: 3, borderBottom: '1px solid #EAECEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F9FAFB' }}>
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                                 <Box sx={{ width: 40, height: 40, bgcolor: '#E5EFFF', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                                     <ViewInArIcon sx={{ color: '#0064FF' }} />
//                                 </Box>
//                                 <Box>
//                                     <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Gilroy-Bold', color: '#333' }}>Traces</Typography>
//                                     <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Gilroy-Regular' }}>Track details of every trace in here</Typography>
//                                 </Box>
//                             </Box>
//                             <IconButton onClick={() => setIsModalOpen(false)}><CloseIcon /></IconButton>
//                         </Box>

//                         {/* Modal Body */}
//                         <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto', bgcolor: '#fff', position: 'relative' }}>
//                             <Box sx={{ mb: 3 }}>
//                                 <TextField
//                                     size="small"
//                                     placeholder="Search by trace ID or name..."
//                                     value={searchQuery}
//                                     onChange={(e) => setSearchQuery(e.target.value)}
//                                     InputProps={{
//                                         startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
//                                         sx: { borderRadius: '8px', fontFamily: 'Gilroy-Regular', width: '300px', bgcolor: '#F5F5F5', '& fieldset': { border: 'none' } }
//                                     }}
//                                 />
//                             </Box>

//                             <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #EAECF0', borderRadius: '8px' }}>
//                                 <Table>
//                                     <TableHead sx={{ bgcolor: '#F9FAFB' }}>
//                                         <TableRow>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Trace ID</TableCell>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Name</TableCell>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Timestamp</TableCell>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Latency</TableCell>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Tokens</TableCell>
//                                             <TableCell sx={{ fontFamily: 'Gilroy-SemiBold', color: '#667085' }}>Cost</TableCell>
//                                         </TableRow>
//                                     </TableHead>
//                                     <TableBody>
//                                         {filteredTraces.map((trace) => (
//                                             <TableRow key={trace.id} hover onClick={() => setSelectedTrace(trace)} sx={{ cursor: 'pointer' }}>
//                                                 <TableCell sx={{ fontFamily: 'monospace', color: '#0064FF' }}>{trace.id}</TableCell>
//                                                 <TableCell sx={{ fontFamily: 'Gilroy-Medium', color: '#333' }}>{trace.name}</TableCell>
//                                                 <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{new Date(trace.timestamp).toLocaleString()}</TableCell>
//                                                 <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{trace.latency}s</TableCell>
//                                                 <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>{trace.totalTokens}</TableCell>
//                                                 <TableCell sx={{ fontFamily: 'Gilroy-Regular', color: '#666' }}>${trace.cost.toFixed(4)}</TableCell>
//                                             </TableRow>
//                                         ))}
//                                         {filteredTraces.length === 0 && (
//                                             <TableRow>
//                                                 <TableCell colSpan={6} align="center" sx={{ py: 4, fontFamily: 'Gilroy-Regular', color: '#666' }}>No traces found.</TableCell>
//                                             </TableRow>
//                                         )}
//                                     </TableBody>
//                                 </Table>
//                             </TableContainer>
//                         </Box>

//                         {/* Slide-out Panel for Detail */}
//                         <TraceDetailPanel open={!!selectedTrace} onClose={() => setSelectedTrace(null)} trace={selectedTrace} />
//                     </Paper>
//                 </Slide>
//             </Modal>
//         </Box>
//     );
// };

// export default LLMTracesSection;